import moment from 'moment';
import {
  isNil,
  sortBy
} from 'lodash';
import { EaqStudentPerformanceDto, EaqStudentTopicPerformanceDto } from '../apis/eaq-app-facade-service/eaq-app-facade-service.dtos';
import { TopicPerformanceTableRow, TopicWithLineage } from '../pages/eaq-student-topic-report/eaq-student-topic-report.models';
import {
  getPercentageAnsweredCorrectly,
  getTimeSpentFormat
} from '../pages/eaq-student-topic-report/eaq-student-topic-report.utilities';
import {
  AssessmentTypeDto,
  AssignmentGradeType,
  AssessmentPerformanceDetailsDto
} from '../apis/eols-assessment-service/eols-assessment-service.dtos';
import { LANGUAGE_KEY } from '../translations/message.constants';
import { AppAction, Application } from '../apis/eols-app-link/eols-app-link.constants';
import { initiateAssessmentApi } from '../apis/eaq-app-facade-service/eaq-app-facade-service.utilities';
import { NavigateToApp } from '../redux/student-study/studentStudy.models';
import { Emdash } from '../constants/studentStudy.constants';
import { ALL_OPTION } from '../pages/eaq-student-topic-report/eaq-student-topic-report.constants';

export const roundByDecimal = (value: number, decimalPlaces: number): number => {
  const multipliedValue = `${value}e${decimalPlaces}`;
  return Number(`${Math.round(Number(multipliedValue))}e-${decimalPlaces}`);
};

export const getStudentPerformance = (topic: EaqStudentTopicPerformanceDto): EaqStudentPerformanceDto => {
  if (
    !topic
    || !topic.studentPerformances
    || !topic.studentPerformances.length
  ) {
    return {
      eolsUser: null,
      masteryLevel: null,
      questionsAnswered: null,
      questionsConfident: null,
      questionsCorrect: null,
      timeSpent: null,
    };
  }

  return topic.studentPerformances[0];
};

export const getTopicTableRows = (flattenedTopics: TopicWithLineage[]): TopicPerformanceTableRow[] => {
  if (!flattenedTopics || !flattenedTopics.length) {
    return null;
  }

  return flattenedTopics.map((topic: TopicWithLineage): TopicPerformanceTableRow => {
    const studentPerformance: EaqStudentPerformanceDto = getStudentPerformance(topic.item);
    return {
      ...studentPerformance,
      topic,
      topicName: topic.item.displayName || topic.item.text,
      lineageDisplay: topic.lineage
        .map((parent: EaqStudentTopicPerformanceDto) => {
          return parent.displayName || parent.text;
        }).join(' / '),
      lineageDisplayOrder: topic.lineage.map((item: EaqStudentTopicPerformanceDto) => {
        return item.displayOrder + (item.displayName || item.text);
      }).join('.'),
      timeSpentDisplay: getTimeSpentFormat(studentPerformance.timeSpent),
      percentCorrect: getPercentageAnsweredCorrectly(studentPerformance)
    };
  });
};

export const getFormattedAssessmentGoal = (
  assessmentType: AssessmentTypeDto,
  assessmentGoal: number,
  messages: Partial<Record<LANGUAGE_KEY, string>>
) => {
  if (assessmentType === AssessmentTypeDto.MASTERY) {
    switch (Math.floor(assessmentGoal)) {
      case 1:
        return messages.NOVICE;
      case 2:
        return messages.INTERMEDIATE;
      case 3:
        return messages.PROFICIENT;
      default:
        return Emdash;
    }
  }
  return assessmentGoal.toString();
};

export const getFormattedMasteryLevel = (
  assessmentType: AssessmentTypeDto,
  masteryLevel: number,
  messages: Partial<Record<LANGUAGE_KEY, string>>
) => {
  if (assessmentType === AssessmentTypeDto.MASTERY) {
    switch (Math.floor(masteryLevel)) {
      case 0:
        return messages.INCOMPLETE;
      case 1:
        return messages.NOVICE;
      case 2:
        return messages.INTERMEDIATE;
      case 3:
        return messages.PROFICIENT;
      default:
        return Emdash;
    }
  }
  return masteryLevel.toString();
};
export const returnNullIfNotGraded = (gradeType: string, numValue: number): number => {
  return gradeType === AssignmentGradeType.NOT_GRADED ? null : numValue;
};

export const getAssessmentScore = (assessmentResults: AssessmentPerformanceDetailsDto): number => {
  if (assessmentResults.totalAnswered === 0) {
    return 0;
  }
  return roundByDecimal((assessmentResults.totalCorrect / assessmentResults.totalAnswered) * 100, 2);
};

export const getFormattedAssignmentGradeType = (
  assessmentGradeType: string,
  assessmentCompleteDate: string,
  assignmentDueDate: string,
  messages: Partial<Record<LANGUAGE_KEY, string>>
) => {
  if (assessmentGradeType === AssignmentGradeType.PASS_FAIL) {
    return moment(moment.utc(assessmentCompleteDate).local())
      .isSameOrBefore(moment.utc(assignmentDueDate).local()) ? messages.PASS : messages.FAIL;
  }
  if (assessmentGradeType === AssignmentGradeType.NOT_GRADED) {
    return messages.NOT_GRADED;
  }
  return '';
};

export const getFormattedAssessmentType = (
  assessmentType: string,
  messages: Partial<Record<LANGUAGE_KEY, string>>
) => {
  switch (assessmentType) {
    case AssessmentTypeDto.MASTERY:
      return messages.MASTERY;
    case AssessmentTypeDto.STANDARD:
      return messages.CUSTOM;
    case AssessmentTypeDto.QUIZ_BY_QUESTION:
      return messages.CUSTOM_QUESTIONS;
    default:
      return Emdash;
  }
};

export const renderFormattedDate = (date: string) => {
  if (!date) {
    return Emdash;
  }

  return `${moment(moment.utc(date).local()).format('D MMM YYYY')} at ${moment(moment.utc(date).local()).format('h:mm a')}`;
};

export const navigateToAssessmentPlayer = (navigateToApp: NavigateToApp, parentLinkId: string, assessmentId: number, returnPage?: string, srcAssessmentId: number = null): void => {
  return navigateToApp({
    action: AppAction.ASSESSMENT_START,
    app: Application.NHE_ASSESSMENT_PLAYER,
    body: {
      assessmentId,
      srcAssessmentId,
      initiateAssessment: {
        url: initiateAssessmentApi(),
        httpMethod: 'POST',
        payload: { assessmentId }
      },
      integrationMetadata: {
        name: 'EAQ'
      },
      ref: returnPage
    },
    altSrcApp: Application.STUDENT_STUDY,
    parentLinkId,
    includeLinkHash: true
  });
};

export const flattenTreeWithLineage = (array: EaqStudentTopicPerformanceDto[], parentLineage: EaqStudentTopicPerformanceDto[] = []): TopicWithLineage[] => {
  return sortBy(array, (item: EaqStudentTopicPerformanceDto) => {
    return item.displayOrder;
  })
    .reduce((acc, cur: EaqStudentTopicPerformanceDto) => {
      const itemLineage = [...parentLineage, cur];
      if (cur.children && cur.children.length) {
        return [...acc, ...flattenTreeWithLineage(cur.children, itemLineage)];
      }
      return [...acc, {
        item: cur,
        lineage: itemLineage
      }];
    }, []);
};

export const flattenedTopicReportTopics = (
  topicPerformances: EaqStudentTopicPerformanceDto[],
  activeTopicOrgVtwId: string
): TopicWithLineage[] => {
  if (!topicPerformances || !topicPerformances.length) {
    return null;
  }

  return flattenTreeWithLineage(
    topicPerformances.filter((item) => {
      if (!activeTopicOrgVtwId || activeTopicOrgVtwId === ALL_OPTION.value) {
        return true;
      }
      return item.vtwId === activeTopicOrgVtwId;
    })
  );
};

export const convertMasteryScoreToPercentage = (score: number) => {
  let _score = score;

  const masteryMax = 4;

  if (isNil(_score)) {
    _score = 0;
  }

  if (_score < 0) {
    _score = 0;
  }

  if (_score > masteryMax) {
    _score = masteryMax;
  }

  _score /= masteryMax;

  if (isNaN(_score)) {
    _score = 0;
  }

  return _score * 100;
};

export const getMasteryLevelLabel = (score: number, messages: Partial<Record<LANGUAGE_KEY, string>>, prefixLabel = messages.MASTERY_LEVEL) => {
  let labelMasteryScore = '';
  if (score < 1) {
    labelMasteryScore = messages.INCOMPLETE;
  } else if (score >= 1 && score < 2) {
    labelMasteryScore = messages.NOVICE;
  } else if (score >= 2 && score < 3) {
    labelMasteryScore = messages.INTERMEDIATE;
  } else {
    labelMasteryScore = messages.PROFICIENT;
  }
  return prefixLabel ? `${prefixLabel}: ${labelMasteryScore}` : labelMasteryScore;
};
